import { ExclamationCircleFilled } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { FileStatus, QuestionResponsePrivate, TestValidationErrorTarget } from '@tests/types';
import { Button, Col, Row } from 'antd';
import classNames from 'classnames';
import { inc } from 'ramda';
import React, { SetStateAction, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Delete, Drag, Trash } from '@/assets';
import { Visible } from '@/components/Visible';
import { ImageSizes } from '@/constants';
import { useDebounce, useDisabledByUpdate } from '@/hooks';
import { validationErrorsTestsSelector } from '@/selectors';
import { useDeleteQuestionMutation } from '@/services';
import { ButtonSize, ButtonType } from '@/types';
import {
  getValidationErrorsByTarget,
  getValidationErrorsByTargetAndId,
  useGetImageUrl,
} from '@/utils';

import styles from './styles.module.scss';

type Props = {
  canDelete: boolean;
  index: number;
  onClick: React.Dispatch<SetStateAction<string | number>>;
  onDelete: (id: string, wasSelected: boolean) => void;
  question: QuestionResponsePrivate;
  selected: boolean;
};

export const QuestionPreview: React.FC<Props> = ({
  canDelete,
  index,
  onClick,
  onDelete,
  question,
  selected,
}) => {
  const { answers, id, media, text } = question;

  const { attributes, listeners } = useSortable({
    id,
  });

  const [deleteQuestion] = useDeleteQuestionMutation();

  const getImageUrl = useGetImageUrl();

  const validationErrors = useSelector(validationErrorsTestsSelector);

  const isDeletingRef = useRef(false);

  const hasErrors =
    getValidationErrorsByTargetAndId(validationErrors, TestValidationErrorTarget.Question, id)
      ?.length > 0;

  const hasAnswerErrors = () => {
    const answersIds = answers?.map((answer) => answer.id);
    const answerErrorsIds = getValidationErrorsByTarget(
      validationErrors,
      TestValidationErrorTarget.Answer,
    )?.map((answerError) => answerError.targetId);

    const answerErrors = answersIds?.filter((answerId) => answerErrorsIds?.includes(answerId));

    return answerErrors?.length > 0;
  };

  const disabled = useDisabledByUpdate();

  const handleDeleteQuestionClick = async () => {
    if (isDeletingRef.current || !canDelete) return;

    isDeletingRef.current = true;
    await deleteQuestion(id).unwrap();
    onDelete?.(id.toString(), selected);
    isDeletingRef.current = false;
  };

  const handleClick = () => {
    onClick(id);
  };

  const isPreviewVisible =
    !!media?.url && media?.status !== FileStatus.Converting && media?.contentType !== 'video/mp4';

  return (
    <div
      className={classNames(
        styles.questionPreview,
        selected && styles.selected,
        disabled && styles.disabled,
      )}
      onClick={handleClick}
    >
      <Row align="middle" style={{ height: '100%' }} gutter={[15, 15]}>
        <Col span="2" className={styles.order}>
          {inc(index)}
        </Col>
        <Col span="19">
          <Row gutter={[10, 10]}>
            <Col span={24} className={styles.questionTitlePreview}>
              {text}
            </Col>
            <Col span={24}>
              <div className={styles.questionCoverPreview}>
                <Visible isVisible={isPreviewVisible}>
                  <img
                    alt=""
                    src={getImageUrl({
                      options: { resize: ImageSizes.questionPreviewMedia },
                      url: media?.url,
                    })}
                  />
                </Visible>
              </div>
            </Col>
          </Row>
        </Col>
        <Col span="3" className={styles.buttons}>
          <Row className={styles.buttonsWrapper} align="middle" justify="space-around">
            <Col className={styles.drag} {...attributes} {...listeners}>
              <Drag />
            </Col>
            {(hasErrors || hasAnswerErrors()) && (
              <ExclamationCircleFilled className={styles.error} />
            )}
            <Button
              type={ButtonType.Text}
              onClick={handleDeleteQuestionClick}
              size={ButtonSize.Small}
              disabled={isDeletingRef.current}
              className={classNames(styles.delete, !canDelete && styles.disabled)}
              icon={<Delete />}
            />
          </Row>
        </Col>
      </Row>
    </div>
  );
};
